<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateBrokerPaymentMethodFields from './RealEstateBrokerPaymentMethodFields.vue';

const props = defineProps({
  catalog: { type: Object, default: () => ({ banks: [], pix_keys: [] }) },
  method: { type: Object, default: null },
  saving: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();
const draft = ref({});

const reset = () => {
  if (props.method) {
    draft.value = {
      method_type: props.method.tipo_pagamento,
      nickname: props.method.apelido || '',
      is_primary: Boolean(props.method.principal),
    };
    return;
  }

  draft.value = {
    method_type: 'pix',
    nickname: '',
    pix_key_type: '',
    pix_key: '',
    is_primary: false,
  };
};

watch(
  () => props.show,
  value => value && reset(),
  { immediate: true }
);

const isBank = computed(() => draft.value.method_type === 'bank');
const isEditing = computed(() => Boolean(props.method?.id));
const dialogTitle = computed(() =>
  isEditing.value
    ? t('REAL_ESTATE_BROKER_COMMISSION.DIALOG.EDIT_TITLE')
    : t('REAL_ESTATE_BROKER_COMMISSION.DIALOG.TITLE')
);
const saveLabel = computed(() =>
  isEditing.value
    ? t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.UPDATE')
    : t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.SAVE')
);
const valid = computed(() => {
  if (isEditing.value) return true;

  if (isBank.value)
    return Boolean(
      draft.value.bank_code && draft.value.branch && draft.value.account_number
    );
  return Boolean(draft.value.pix_key_type && draft.value.pix_key);
});

const selectMethod = method => {
  const fields =
    method === 'bank'
      ? ['pix_key_type', 'pix_key']
      : ['bank_code', 'branch', 'account_number'];
  const nextDraft = { ...draft.value, method_type: method };
  fields.forEach(field => delete nextDraft[field]);
  draft.value = nextDraft;
};

const updateDraft = (field, value) => {
  draft.value = { ...draft.value, [field]: value };
};

const save = () => {
  if (valid.value) emit('save', { ...draft.value });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="dialogTitle"
    max-width-class="max-w-[560px]"
    @close="emit('close')"
  >
    <div class="grid gap-4">
      <fieldset v-if="!isEditing" class="grid grid-cols-2 gap-3">
        <button
          v-for="paymentType in ['pix', 'bank']"
          :key="paymentType"
          type="button"
          class="rounded-lais border p-3 text-left text-sm font-semibold"
          :class="
            draft.method_type === paymentType
              ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
              : 'border-lais-border text-lais-text'
          "
          @click="selectMethod(paymentType)"
        >
          {{
            paymentType === 'pix'
              ? t('REAL_ESTATE_BROKER_COMMISSION.DIALOG.PIX')
              : t('REAL_ESTATE_BROKER_COMMISSION.DIALOG.BANK')
          }}
        </button>
      </fieldset>
      <LaisField :label="t('REAL_ESTATE_BROKER_COMMISSION.DIALOG.NICKNAME')">
        <input
          v-model="draft.nickname"
          name="nickname"
          class="min-h-10 rounded-lais border border-lais-border px-3 text-sm"
        />
      </LaisField>
      <RealEstateBrokerPaymentMethodFields
        v-if="!isEditing"
        :catalog="catalog"
        :draft="draft"
        :is-bank="isBank"
        @update="updateDraft"
      />
      <label class="flex items-center gap-2 text-sm text-lais-text-body">
        <input
          v-model="draft.is_primary"
          class="size-4 accent-lais-primary"
          type="checkbox"
        />
        {{ t('REAL_ESTATE_BROKER_COMMISSION.DIALOG.PRIMARY') }}
      </label>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.BACK') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :disabled="!valid"
          :is-loading="saving"
          @click="save"
        >
          {{ saveLabel }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
